import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import AddressSearch from "./AddressSearch";

type ServiceOption = { id: string; label: string; duration_minutes: number | null; price: string | number | null };
type Service = { id: string; category: string; name: string; base_duration_minutes: number; base_price: string | number | null; at_client_address: boolean; options: ServiceOption[] };
type Staff = { id: string; user_id: string; name: string; avatar_url: string | null };
type Friend = { id: string; name: string; avatar_url?: string };
type Slot = { date: string; day_of_week: number; start_time: string; end_time: string; price: number | null; staff_member_id: string; staff_name: string | null; staff_avatar: string | null };

const DAY_LABELS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const fmtDate = (iso: string) => {
  const d = new Date(iso + "T00:00:00");
  return `${DAY_LABELS[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
};

// Réservation d'une prestation (agenda nouvelle génération) — port fidèle de
// OLTODO/components/ServiceBookingModal.tsx : 4 étapes (prestation, option si applicable,
// spécialiste si le lieu en compte plusieurs pour cette prestation — sinon étape sautée
// automatiquement —, créneau déjà résolu côté serveur). Composant distinct de BookingModal.tsx
// (mode simple).
export default function ServiceBookingModal({ venueId, venueName, averageRating, reviewsCount, onClose, onBooked }: {
  venueId: string; venueName: string; averageRating?: number | null; reviewsCount?: number | null; onClose: () => void; onBooked: () => void;
}) {
  const { lang, user } = useApp();
  const [step, setStep] = useState<"service" | "option" | "staff" | "slot">("service");
  const [services, setServices] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [service, setService] = useState<Service | null>(null);
  const [option, setOption] = useState<ServiceOption | null>(null);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [chosenStaff, setChosenStaff] = useState<Staff | null>(null);
  const [staffPicked, setStaffPicked] = useState(false);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selected, setSelected] = useState<Slot | null>(null);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // Livraison à domicile — uniquement affiché si la prestation le propose (voir
  // agenda_at_client_address_label). Pas de temps de trajet modélisé, juste l'adresse capturée
  // pour que le prestataire s'organise.
  const [deliveryMode, setDeliveryMode] = useState<"venue" | "client">("venue");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryCoords, setDeliveryCoords] = useState<{ lat: number | null; lon: number | null }>({ lat: null, lon: null });

  // Pour qui ? — purement informatif pour le prestataire, le demandeur reste seul responsable de
  // la réservation (voir migrate.ts).
  const [forWhom, setForWhom] = useState<"me" | "friend" | "company">("me");
  const [friends, setFriends] = useState<Friend[]>([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [friendId, setFriendId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState("");

  useEffect(() => {
    api.get(`/venues/${venueId}/services`).then(data => setServices(data?.services || [])).catch(() => {}).finally(() => setLoadingServices(false));
  }, [venueId]);

  const pickForWhom = (w: "me" | "friend" | "company") => {
    setForWhom(w);
    if (w === "friend" && friends.length === 0 && user?.id) {
      setLoadingFriends(true);
      api.get(`/users/${user.id}/friends`).then(data => setFriends(data?.friends || [])).catch(() => setFriends([])).finally(() => setLoadingFriends(false));
    }
  };

  const chooseService = (s: Service) => {
    setService(s); setOption(null);
    if (s.options.length > 0) setStep("option");
    else goToStaffOrSlot(s.id, null);
  };
  const chooseOption = (o: ServiceOption | null) => {
    setOption(o);
    if (service) goToStaffOrSlot(service.id, o?.id || null);
  };
  // Étape spécialiste sautée automatiquement s'il n'y en a qu'un (ou aucun) — inutile de choisir
  // entre une seule option.
  const goToStaffOrSlot = (serviceId: string, optionId: string | null) => {
    setLoadingStaff(true);
    api.get(`/venues/${venueId}/services/${serviceId}/staff`).then(data => {
      const list: Staff[] = data?.staff || [];
      setStaffList(list);
      if (list.length > 1) {
        setChosenStaff(null); setStaffPicked(false);
        setStep("staff");
      } else {
        setStep("slot");
        loadSlots(serviceId, optionId, null);
      }
    }).catch(() => { setStaffList([]); setStep("slot"); loadSlots(serviceId, optionId, null); }).finally(() => setLoadingStaff(false));
  };
  const chooseStaff = (s: Staff | null) => {
    setChosenStaff(s); setStaffPicked(true);
    setStep("slot");
    if (service) loadSlots(service.id, option?.id || null, s?.id || null);
  };
  const loadSlots = (serviceId: string, optionId: string | null, staffMemberId: string | null) => {
    setLoadingSlots(true); setSelected(null);
    const params = new URLSearchParams();
    if (optionId) params.set("option_id", optionId);
    if (staffMemberId) params.set("staff_member_id", staffMemberId);
    const qs = params.toString() ? `?${params.toString()}` : "";
    api.get(`/venues/${venueId}/services/${serviceId}/slots${qs}`).then(data => setSlots(data?.slots || [])).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
  };

  const distinctStaff = new Set(slots.map(s => s.staff_member_id)).size;

  const submit = () => {
    if (!selected || !service || submitting) return;
    setSubmitting(true); setError("");
    const body: any = {
      booking_date: selected.date, start_time: selected.start_time, staff_member_id: selected.staff_member_id,
      option_id: option?.id || null, message: message.trim() || undefined, lang,
    };
    if (service.at_client_address && deliveryMode === "client" && deliveryAddress.trim()) {
      body.delivery_address = deliveryAddress.trim();
      body.delivery_latitude = deliveryCoords.lat;
      body.delivery_longitude = deliveryCoords.lon;
    }
    if (forWhom === "friend" && friendId) body.on_behalf_of_user_id = friendId;
    else if (forWhom === "company" && companyName.trim()) body.company_name = companyName.trim();
    if (selected.price != null && Number(selected.price) > 0) {
      body.success_url = `${window.location.origin}${window.location.pathname}?payment=success`;
      body.cancel_url = `${window.location.origin}${window.location.pathname}?payment=cancelled`;
    }
    api.post(`/venues/${venueId}/services/${service.id}/bookings`, body)
      .then(data => {
        if (data?.checkoutUrl) window.location.href = data.checkoutUrl;
        else if (data?.booking) { setDone(true); onBooked(); setTimeout(onClose, 1500); }
        else setError(data?.error || t(lang, "error_title"));
      })
      .catch(() => setError(t(lang, "error_connection")))
      .finally(() => setSubmitting(false));
  };

  const categories = Array.from(new Set(services.map(s => s.category)));
  const back = () => {
    if (step === "slot") { setStep(staffList.length > 1 ? "staff" : service && service.options.length > 0 ? "option" : "service"); return; }
    if (step === "staff") { setStep(service && service.options.length > 0 ? "option" : "service"); return; }
    setStep("service");
  };
  const stepTitle = step === "service" ? t(lang, "svc_booking_choose_service") : step === "option" ? t(lang, "svc_booking_choose_option") : step === "staff" ? t(lang, "svc_booking_choose_staff") : t(lang, "svc_booking_choose_slot");

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 440, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          {step !== "service" && !done && (
            <button onClick={back} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, color: "#333" }}>←</button>
          )}
          <span style={{ fontSize: 17, fontWeight: 700, flex: 1 }}>{stepTitle}</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: "#999", marginBottom: averageRating != null ? 4 : 18 }}>
          {venueName}{service ? ` · ${service.name}` : ""}{option ? ` (${option.label})` : ""}{staffPicked ? ` · ${chosenStaff ? chosenStaff.name : t(lang, "svc_booking_any_staff")}` : ""}
        </div>
        {averageRating != null && (
          <div style={{ fontSize: 12, color: "#999", marginBottom: 18 }}>★ {Number(averageRating).toFixed(1)}/10 · {reviewsCount} {t(lang, "venue_reviews_suffix")}</div>
        )}

        {done ? (
          <div style={{ padding: "30px 0", textAlign: "center" }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>✓</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{t(lang, "booking_request_sent")}</div>
          </div>
        ) : step === "service" ? (
          loadingServices ? (
            <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "loading_ellipsis")}</div>
          ) : categories.length === 0 ? (
            <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "svc_booking_no_services")}</div>
          ) : (
            categories.map(cat => (
              <div key={cat} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#999", marginBottom: 8 }}>{cat}</div>
                {services.filter(s => s.category === cat).map(s => (
                  <div key={s.id} onClick={() => chooseService(s)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #E0E0E0", borderRadius: 12, padding: 12, marginBottom: 8, cursor: "pointer" }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{s.base_duration_minutes} min{s.base_price != null ? `, ${Number(s.base_price).toFixed(2)} €` : ""}</div>
                    </div>
                    <span style={{ color: "#999" }}>›</span>
                  </div>
                ))}
              </div>
            ))
          )
        ) : step === "option" && service ? (
          service.options.map(o => (
            <div key={o.id} onClick={() => chooseOption(o)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", border: "1px solid #E0E0E0", borderRadius: 12, padding: 12, marginBottom: 8, cursor: "pointer" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{o.label}</div>
                <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
                  {o.duration_minutes ?? service.base_duration_minutes} min, {(o.price != null ? Number(o.price) : service.base_price != null ? Number(service.base_price) : 0).toFixed(2)} €
                </div>
              </div>
              <span style={{ color: "#999" }}>›</span>
            </div>
          ))
        ) : step === "staff" ? (
          loadingStaff ? (
            <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "loading_ellipsis")}</div>
          ) : (
            <>
              <div onClick={() => chooseStaff(null)} style={{ display: "flex", alignItems: "center", gap: 12, border: "1px solid #E0E0E0", borderRadius: 12, padding: 10, marginBottom: 8, cursor: "pointer" }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: "#999", fontSize: 18 }}>↔</div>
                <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{t(lang, "svc_booking_any_staff")}</span>
                <span style={{ color: "#999" }}>›</span>
              </div>
              {staffList.map(s => (
                <div key={s.id} onClick={() => chooseStaff(s)} style={{ display: "flex", alignItems: "center", gap: 12, border: "1px solid #E0E0E0", borderRadius: 12, padding: 10, marginBottom: 8, cursor: "pointer" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                    {s.avatar_url ? <img src={s.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (s.name || "?")[0]}
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 600, flex: 1 }}>{s.name}</span>
                  <span style={{ color: "#999" }}>›</span>
                </div>
              ))}
            </>
          )
        ) : loadingSlots ? (
          <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "loading_ellipsis")}</div>
        ) : slots.length === 0 ? (
          <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>{t(lang, "no_slots_available")}</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 18 }}>
              {slots.map((s, i) => {
                const isSel = selected?.date === s.date && selected?.start_time === s.start_time && selected?.staff_member_id === s.staff_member_id;
                return (
                  <button key={i} onClick={() => setSelected(s)} style={{ padding: "10px 6px", borderRadius: 10, border: isSel ? "1.5px solid #0a0a0a" : "1px solid #E0E0E0", backgroundColor: isSel ? "#0a0a0a" : "white", color: isSel ? "white" : "#333", cursor: "pointer", textAlign: "center" }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700 }}>{fmtDate(s.date)}</div>
                    <div style={{ fontSize: 11.5, marginTop: 2, opacity: 0.85 }}>{s.start_time.slice(0, 5)}</div>
                    {distinctStaff > 1 && !!s.staff_name && <div style={{ fontSize: 10.5, marginTop: 2, opacity: 0.85, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.staff_name}</div>}
                    {s.price != null && <div style={{ fontSize: 11.5, marginTop: 2, fontWeight: 700 }}>{Number(s.price).toFixed(2)} €</div>}
                  </button>
                );
              })}
            </div>

            {service?.at_client_address && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  {(["venue", "client"] as const).map(m => (
                    <button key={m} onClick={() => setDeliveryMode(m)} style={{ padding: "8px 14px", borderRadius: 18, cursor: "pointer", fontSize: 12.5, fontWeight: 600, border: deliveryMode === m ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: deliveryMode === m ? "#0a0a0a" : "white", color: deliveryMode === m ? "white" : "#666" }}>
                      {t(lang, m === "venue" ? "svc_booking_delivery_venue" : "svc_booking_delivery_client")}
                    </button>
                  ))}
                </div>
                {deliveryMode === "client" && (
                  <AddressSearch
                    value={deliveryAddress}
                    onChange={(label, lat, lon) => { setDeliveryAddress(label); setDeliveryCoords({ lat, lon }); }}
                    placeholder={t(lang, "svc_booking_delivery_address_placeholder")}
                    showSavedAddresses
                  />
                )}
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#999", marginBottom: 8 }}>{t(lang, "svc_booking_for_whom_label")}</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                {(["me", "friend", "company"] as const).map(w => (
                  <button key={w} onClick={() => pickForWhom(w)} style={{ padding: "8px 14px", borderRadius: 18, cursor: "pointer", fontSize: 12.5, fontWeight: 600, border: forWhom === w ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: forWhom === w ? "#0a0a0a" : "white", color: forWhom === w ? "white" : "#666" }}>
                    {t(lang, w === "me" ? "svc_booking_for_me" : w === "friend" ? "svc_booking_for_friend" : "svc_booking_for_company")}
                  </button>
                ))}
              </div>
              {forWhom === "friend" && (
                loadingFriends ? (
                  <div style={{ fontSize: 12.5, color: "#999" }}>{t(lang, "loading_ellipsis")}</div>
                ) : (
                  <>
                    <div style={{ fontSize: 11.5, color: "#999", marginBottom: 6 }}>{t(lang, "svc_booking_choose_friend")}</div>
                    <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4 }}>
                      {friends.map(f => (
                        <div key={f.id} onClick={() => setFriendId(prev => prev === f.id ? null : f.id)} style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 56, flexShrink: 0, cursor: "pointer" }}>
                          <div style={{ width: 44, height: 44, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13, border: friendId === f.id ? "2.5px solid #0a0a0a" : "2.5px solid transparent" }}>
                            {f.avatar_url ? <img src={f.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (f.name || "?")[0]}
                          </div>
                          <span style={{ fontSize: 10.5, color: "#666", marginTop: 4, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>{(f.name || "").split(" ")[0]}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )
              )}
              {forWhom === "company" && (
                <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder={t(lang, "svc_booking_company_name_placeholder")} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              )}
            </div>

            <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder={t(lang, "booking_message_placeholder")} maxLength={300} style={{ width: "100%", minHeight: 60, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 16, boxSizing: "border-box", resize: "none" as const }} />
            {!!error && <div style={{ color: "#c0392b", fontSize: 12.5, marginBottom: 8 }}>{error}</div>}
            <button onClick={submit} disabled={!selected || submitting} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: (!selected || submitting) ? 0.5 : 1 }}>
              {submitting ? "..." : (selected?.price != null && Number(selected.price) > 0) ? t(lang, "pay_and_book") : t(lang, "request_booking")}
            </button>
          </>
        )}
      </div>
    </div>
  ,
    document.body
  );
}
